import type {AgentProfile, ChatInputSlotProps, LeadingIconSlotProps} from '../agent-profile'
import type {TrailingVisualSlotProps} from '../agent-profile-types'
import {AGENT_INFO_REGISTRY} from '../agent-profile'
import {JetBrainsSessionIcon} from '../../components/JetBrainsSessionIcon'
import {JetBrainsIcon} from '../../components/CustomIcons'
import {EmptyCliSession} from '../../components/session-logs/EmptyCliSession'
import {CLIChatInput} from '../../components/task-view/CLIChatInput'
import {computeConnectionStatus} from '../../utils/connection-status'
import {ConnectionStatus} from '../../types/session'
import type {SessionState} from '../../types/session'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {REMOTABLE_BASE} from './remotable-base'
import trailingStyles from '../../components/AgentTypeTrailingVisual.module.css'

function JetBrainsLeadingIcon({staleAt, dataUpdatedAt, state}: LeadingIconSlotProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, state as SessionState, dataUpdatedAt)
  const isConnected = connectionStatus === ConnectionStatus.Connected

  return <JetBrainsSessionIcon showConnectedDot={isConnected} />
}

function JetBrainsTrailingVisual({state, staleAt, dataUpdatedAt}: TrailingVisualSlotProps) {
  if (!isFeatureEnabled('copilot_mission_control_session_filters')) {
    return null
  }

  const connectionStatus = computeConnectionStatus(staleAt, true, state, dataUpdatedAt)
  const isConnected = connectionStatus === ConnectionStatus.Connected
  const label = isConnected ? 'JetBrains session connected' : 'JetBrains session'

  return (
    <span
      className={trailingStyles.wrapper}
      role="img"
      aria-label={label}
      data-testid="agent-type-trailing-visual-jetbrains"
    >
      <JetBrainsIcon />
      {isConnected && <span data-testid="connected-dot" className={trailingStyles.connectedDot} aria-hidden="true" />}
    </span>
  )
}

function RemotableChatInputSlot({
  session,
  task,
  onCommandSent,
  cliRemoteNoticeDismissed,
  remoteControlPolicyEnabled,
  ssoOrgIds,
}: ChatInputSlotProps) {
  const remoteSteerable = task?.remote_steerable === true
  const agentTaskId = task?.agent_collaborators?.[0]?.agent_task_id
  return (
    <CLIChatInput
      session={session}
      remoteSteerable={remoteSteerable}
      remoteControlPolicyEnabled={remoteControlPolicyEnabled}
      ssoOrgIds={ssoOrgIds}
      agentTaskId={agentTaskId}
      staleAt={task?.stale_at}
      onCommandSent={onCommandSent}
      cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
    />
  )
}

export const JETBRAINS_PROFILE: Partial<AgentProfile> = {
  ...REMOTABLE_BASE,
  slug: 'jetbrains-chat',
  displayName: 'Copilot in JetBrains',
  secondaryLabel: 'JetBrains',
  agentInfo: AGENT_INFO_REGISTRY['jetbrains-chat'],
  slots: {
    LeadingIcon: JetBrainsLeadingIcon,
    ChatInput: RemotableChatInputSlot,
    EmptyState: EmptyCliSession,
    TrailingVisual: JetBrainsTrailingVisual,
  },
}
